import React from 'react';
import { times } from 'lodash';
import { compose } from 'recompose';
import { CalendarGrid, CalendarListV2 } from './lib';
import { serviceLocator } from 'infrastructure/ServiceLocator';
import LayoutHelper from '../../infrastructure/LayoutHelper';
import routes from 'library/Routes';
import { ScheduleDayHeaderView } from './StudioScheduleView/ScheduleDayHeaderView';
import { ScheduleHourTitleView } from './StudioScheduleView/ScheduleHourTitleView';
import { PublicClassEventCellView } from './StudioScheduleView/PublicClassEventCellView';
import { ClassEventListView } from './StudioScheduleView/ClassEventListView';
import { ScheduleDayListView } from './StudioScheduleView/ScheduleDayListView';
import {
  Grid,
  withStyles,
  IconButton,
  Typography,
  Popover,
  FormControlLabel,
  Checkbox,
  Chip,
  Theme,
} from '@material-ui/core';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import { withLocale } from '../../hocs/withLocale';
import CalendarToday from '@material-ui/icons/CalendarToday';
import ListIcon from '@material-ui/icons/List';
import FilterIcon from '@material-ui/icons/FilterList';
import { isClassInfoCanceled } from '../../utils/attendance';
import moment from 'moment';
import {
  CalendarTeacher,
  CalendarEvent,
  ClassInfo,
  Studio,
  StudioPublicScheduleCalendarProps,
  Schedule,
  StudioPublicScheduleCalendarState,
  CalendarFilter,
} from '../../types';

const daysInWeek = 7;
const startOfThisWeek = moment.utc().startOf('isoWeek');
const nextWeek = (date: moment.Moment): moment.Moment => moment(date).add(daysInWeek, 'days');
const previousWeek = (date: moment.Moment): moment.Moment => moment(date).add(-daysInWeek, 'days');
const defaultColor = 'wheat';

const createEventData = (
  { event, classInfo }: { event?: CalendarEvent; classInfo?: ClassInfo },
  schedule: Schedule,
  teachersMap: { [key: string]: CalendarTeacher },
  studio: Studio,
  currentLanguage: string,
  translate: (key: string, options?: any) => string,
  owner?: { timezone: string; [key: string]: any }
): CalendarEvent | undefined => {
  let calendarEvent: CalendarEvent = {
    id: '', // Will be assigned later
    eventId: '', // Will be assigned later
    classInfo,
    event,
    color: defaultColor,
    showTitle: true,
    start: moment(), // Will be assigned later
    end: moment(), // Will be assigned later
    title: '', // Will be assigned later
    private: false, // Will be assigned later
  };

  const couresMap = schedule.repeatableEventsMap;
  if (event) {
    const course = couresMap && couresMap[event.repeatableEventId];
    const billingTag = event.billingTag;
    calendarEvent.rooms = event.room.map((roomId) => {
      return schedule.roomsMap[roomId];
    });
    calendarEvent.borderColor = event.room.color;
    let classDateTimeMoment = moment.utc(event.start).locale(currentLanguage);
    event.startMoment = classDateTimeMoment;
    calendarEvent.start = moment.utc(event.start).startOf('minute').add(1, 'millisecond');
    calendarEvent.eventId = event._id;
    calendarEvent.showTitle = event.showTitle;
    calendarEvent.duration = event.duration;
    calendarEvent.billingTag = event.billingTag;
    calendarEvent.limit = event.limit;
    calendarEvent.private = event.private;
    if (event.duration) {
      calendarEvent.end = moment.utc(event.start).add(event.duration, 'minute').startOf('minute');
    } else {
      calendarEvent.end = moment.utc(event.end!).startOf('minute');
    }
    if (event.private) {
      calendarEvent.title = translate('studio.privateClass');
    } else {
      if (event.teacherId) {
        let teachers = [];
        for (let i = 0; i < event.teacherId.length; i++) {
          let teacher = teachersMap[event.teacherId[i]];
          if (teacher) {
            teachers.push(teacher);
          }
        }
        calendarEvent.teachers = teachers;
      }
      calendarEvent.instructorName = event.instructorName;
      calendarEvent.note = event.note;
      calendarEvent.title = event.title;
      calendarEvent.color = event.color || defaultColor;
    }
  } else {
    event = {} as CalendarEvent;
  }
  calendarEvent.id = LayoutHelper.guid();
  if (classInfo) {
    if (isClassInfoCanceled(classInfo)) {
      return undefined;
    }
    const course = couresMap && couresMap[classInfo.courseId!];
    const billingTag = classInfo.billingTag;
    calendarEvent.start = moment.utc(classInfo.classTime).startOf('minute').add(1, 'millisecond');
    calendarEvent.eventId = classInfo.eventId || '';
    let duration = classInfo.duration;
    if (!duration) {
      duration = 60;
    }
    if (!classInfo.eventId) {
      calendarEvent.color = classInfo.color || defaultColor;
    }
    calendarEvent.room = schedule.roomsMap[classInfo.roomId[0]];
    calendarEvent.rooms = classInfo.roomId.map((roomId) => {
      return schedule.roomsMap[roomId];
    });
    calendarEvent.duration = duration;
    calendarEvent.limit = classInfo.limit;
    calendarEvent.registeredCount = classInfo.registeredCount;
    if (calendarEvent.limit && calendarEvent.start.diff(moment.utc()) > 0) {
      let availableSpots = calendarEvent.limit - (calendarEvent.registeredCount || 0);
      calendarEvent.spotsLeft = `😀${translate('studio.classAvailability', {
        availableSpots: availableSpots,
      })}`;
      if (availableSpots === 0) {
        calendarEvent.spotsLeft = `😡${translate('studio.classBookedOut')}`;
        calendarEvent.noSpots = true;
      }
    }
    calendarEvent.end = moment
      .utc(calendarEvent.start)
      .add(duration, 'minute')
      .startOf('minute')
      .locale(currentLanguage);
    calendarEvent.private = classInfo.private;
    if (classInfo.private) {
      calendarEvent.title = translate('studio.privateClass');
    } else {
      calendarEvent.title = classInfo.className;
      calendarEvent.instructorName = classInfo.instructorName;
      calendarEvent.note = classInfo.note;

      if (classInfo.allowSelfSignUp) {
        let link = routes.getClassLink(studio, classInfo.groupId || '');
        calendarEvent.signUpLink = link;
        calendarEvent.noSignUp = false;
        const isClassBookable = LayoutHelper.isClassBookable(
          owner ? owner.timezone : moment.tz.guess(),
          studio,
          calendarEvent.start
        );
        if (!isClassBookable) {
          calendarEvent.noSignUp = true;
        }
      }
      if (classInfo.teacherId) {
        let teachers = [];
        for (let i = 0; i < classInfo.teacherId.length; i++) {
          let teacher = teachersMap[classInfo.teacherId[i]];
          if (teacher) {
            teachers.push(teacher);
          }
        }
        calendarEvent.teachers = teachers;
      }
    }
  }
  return calendarEvent;
};

export const StudioPublicScheduleCalendar = compose(
  withStyles((theme: Theme) => ({
    events: {
      flex: 1,
      minHeight: theme.spacing(10),
    },
    buttons: {
      marginBottom: theme.spacing(2),
      '& > *': {
        flex: 1,
      },
    },
    button: {
      marginRight: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    filterGrid: {
      padding: theme.spacing(1),
    },
  })),
  withLocale
)(
  class StudioPublicScheduleCalendarComponent extends React.Component<
    StudioPublicScheduleCalendarProps,
    StudioPublicScheduleCalendarState
  > {
    constructor(props: StudioPublicScheduleCalendarProps) {
      super(props);

      let courseId = props.courseId;
      let teacherId = props.teacherId;

      const filter: CalendarFilter = { courses: {}, teacherIds: {} };
      if (courseId) {
        for (const course of props.schedule.repeatableEvents) {
          if (course._id === courseId) {
            filter.courses[courseId] = course;
          }
        }
      }
      if (teacherId) {
        const teacher = this.props.teachersMap[teacherId];
        if (teacher) {
          filter.teacherIds[teacherId] = teacher;
        }
      }
      this.state = {
        listView: LayoutHelper.isMobile(),
        events: null,
        from: startOfThisWeek,
        selectedDate: moment.utc(),
        filter: filter,
      };
    }

    componentWillMount() {
      this.fetchDate();
    }

    componentDidUpdate() {
      this.fetchDate();
    }

    fetchDate = (): void => {
      const { schedule, teachersMap, studio } = this.props;
      const { from, events } = this.state;
      const { currentLanguage, translate, owner } = this.props;
      if (!schedule || !teachersMap || !studio) {
        return;
      }
      if (events) {
        return;
      }
      serviceLocator.studioApi.getPublicScheduleOnTheWeek(studio.id, from, nextWeek(from)).then((response) => {
        const allEvents = response.classes
          .map((classEvent: { event?: CalendarEvent; classInfo?: ClassInfo }) =>
            createEventData(classEvent, schedule, teachersMap, studio, currentLanguage, translate, owner)
          )
          .filter((e: CalendarEvent | undefined) => !!e) as CalendarEvent[];
        const events = this.filterEvents(this.state.filter, allEvents);
        this.setState({
          events: events,
          allEvents: allEvents,
        });
      });
    };

    filterEvents = (filter: CalendarFilter, allEvents: CalendarEvent[] | null): CalendarEvent[] => {
      let result: CalendarEvent[] = [];
      if (allEvents) {
        for (let event of allEvents) {
          let matched = false;
          if (filter && filter.courses && Object.keys(filter.courses).length > 0) {
            const scheduleEvent = event.event;
            if (scheduleEvent && filter.courses[scheduleEvent.repeatableEventId]) {
              matched = true;
            }
          } else {
            matched = true;
          }
          if (!matched) {
            continue;
          }
          if (filter && filter.teacherIds && Object.keys(filter.teacherIds).length > 0) {
            let matchTeahcer = false;
            if (event.teachers) {
              for (let teacher of event.teachers) {
                if (filter.teacherIds[teacher._id]) {
                  matched = true;
                  matchTeahcer = true;
                  break;
                }
              }
            }
            if (!matchTeahcer) {
              continue;
            }
          } else {
            matched = true;
          }
          if (matched) {
            result.push(event);
          }
        }
      }
      return result;
    };

    changeStartDate = (newStartDate: moment.Moment) => () => {
      this.setState({
        events: null,
        from: newStartDate,
        selectedDate: newStartDate,
      });
    };

    changeStartDateTodate = (newStartDate: moment.Moment) => () => {
      this.setState({
        events: null,
        from: newStartDate,
        selectedDate: moment.utc(),
      });
    };

    get dates(): moment.Moment[] {
      const { from } = this.state;
      const {
        studio: { workingHours },
      } = this.props;
      if (workingHours) {
        return times(daysInWeek, (day) => (workingHours.days[day] ? moment(from).add(day, 'days') : null)).filter(
          (d) => !!d
        ) as moment.Moment[];
      }

      return times(daysInWeek, (day) => moment(from).add(day, 'days'));
    }

    get calendarProps() {
      const { events } = this.state;
      const {
        studio: { workingHours },
      } = this.props;
      const defaultSettings = {
        startHour: 0,
        endHour: 24,
        events,
      };
      if (workingHours) {
        const startHour = workingHours.from ? moment.utc(workingHours.from).hour() : defaultSettings.startHour;
        const endHour = workingHours.to ? moment.utc(workingHours.to).hour() : defaultSettings.endHour;
        return { startHour, endHour, events };
      }
      return defaultSettings;
    }

    renderEvents = () => {
      const { events, listView } = this.state;
      const { onEventClick, localMoment, owner } = this.props;
      if (!events) {
        return LayoutHelper.renderLoader();
      }
      if (listView) {
        return (
          <CalendarListV2
            {...this.calendarProps}
            dates={this.dates}
            selectedDate={this.state.selectedDate}
            renderDate={(date: moment.Moment) => <ScheduleDayListView date={localMoment(date)} />}
            renderEvent={(event: CalendarEvent) => (
              <ClassEventListView owner={owner} event={event} onClick={onEventClick} studio={this.props.studio} />
            )}
          />
        );
      }
      return (
        <CalendarGrid
          {...this.calendarProps}
          columns={this.dates}
          renderTime={(date: moment.Moment) => <ScheduleHourTitleView hour={localMoment(date)} />}
          renderColumnName={(date: moment.Moment) => <ScheduleDayHeaderView date={localMoment(date)} />}
          renderEvent={(event: CalendarEvent) => (
            <PublicClassEventCellView owner={owner} event={event} onClick={onEventClick} studio={this.props.studio} />
          )}
        />
      );
    };

    render() {
      const { from, listView } = this.state;
      const { classes, translate, localMoment, groupCourses } = this.props;
      return (
        <Grid container direction="column">
          <Grid item xs={12}>
            <Grid container justify="space-between" spacing={1}>
              <Grid item>
                <Grid container wrap="nowrap" alignItems="flex-start">
                  <Grid item>
                    <IconButton
                      color="secondary"
                      size="large"
                      disabled={listView}
                      onClick={() => this.setState({ listView: true })}
                    >
                      <ListIcon />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <IconButton
                      color="secondary"
                      size="large"
                      disabled={!listView}
                      onClick={() => this.setState({ listView: false })}
                    >
                      <CalendarToday />
                    </IconButton>
                  </Grid>
                  <Grid item>
                    <IconButton
                      color="secondary"
                      size="large"
                      onClick={(e) => this.setState({ filterAnchor: e.currentTarget })}
                    >
                      <FilterIcon />
                    </IconButton>
                  </Grid>
                  {this.state.filterAnchor && (
                    <Popover
                      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                      open
                      anchorEl={this.state.filterAnchor}
                      onClose={() => {
                        this.setState({ filterAnchor: null });
                      }}
                    >
                      <Grid container spacing={1} className={classes.filterGrid}>
                        <Grid item xs={12}>
                          {this.props.translate('studio.courses')}
                        </Grid>
                        {Object.values(this.props.groupCourses).map((courseData) => {
                          const course = courseData.course;
                          if (course.private) {
                            return null;
                          }
                          if (course.allowBookEmptySlots) {
                            return null;
                          }
                          return (
                            <Grid item xs={12} key={course._id}>
                              <FormControlLabel
                                label={course.title}
                                control={
                                  <Checkbox
                                    checked={this.state.filter.courses && !!this.state.filter.courses[course._id]}
                                    onChange={(e) => {
                                      const filter = { ...this.state.filter };
                                      for (let item of courseData.items) {
                                        if (filter.courses[item._id]) {
                                          delete filter.courses[item._id];
                                        } else {
                                          filter.courses[item._id] = item;
                                        }
                                      }

                                      this.setState({ filter: filter });
                                      const events = this.filterEvents(filter, this.state.allEvents);
                                      this.setState({ events: events });
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          );
                        })}
                        <Grid item xs={12}>
                          {this.props.translate('studio.teachers')}
                        </Grid>
                        {Object.values(this.props.teachersMap).map((teacher) => {
                          return (
                            <Grid item xs={12} key={teacher._id}>
                              <FormControlLabel
                                label={teacher.name}
                                control={
                                  <Checkbox
                                    checked={
                                      this.state.filter.teacherIds && !!this.state.filter.teacherIds[teacher._id]
                                    }
                                    onChange={(e) => {
                                      const filter = { ...this.state.filter };
                                      if (filter.teacherIds[teacher._id]) {
                                        delete filter.teacherIds[teacher._id];
                                      } else {
                                        filter.teacherIds[teacher._id] = teacher;
                                      }

                                      this.setState({ filter: filter });
                                      const events = this.filterEvents(filter, this.state.allEvents);
                                      this.setState({ events: events });
                                    }}
                                  />
                                }
                              />
                            </Grid>
                          );
                        })}
                      </Grid>
                    </Popover>
                  )}
                </Grid>
              </Grid>
              <Grid item>
                <Typography variant="h6">
                  <IconButton
                    size="small"
                    disabled={moment.utc().startOf('day').diff(from) > 0}
                    onClick={this.changeStartDate(previousWeek(from))}
                  >
                    <KeyboardArrowLeft />
                  </IconButton>
                  {localMoment(from).format('DD MMM')}- {localMoment(from).add(1, 'week').format('DD MMM YYYY')}
                  <IconButton size="small" onClick={this.changeStartDate(nextWeek(from))}>
                    <KeyboardArrowRight />
                  </IconButton>
                </Typography>
              </Grid>
              <Grid item xs={12}>
                {Object.keys(this.state.filter.courses).map((courseId) => {
                  const course = this.state.filter.courses[courseId];
                  return (
                    <Chip
                      key={courseId}
                      label={course.title}
                      onDelete={() => {
                        const filter = { ...this.state.filter };
                        delete filter.courses[courseId];
                        this.setState({ filter: filter });
                        const events = this.filterEvents(filter, this.state.allEvents);
                        this.setState({ events: events });
                      }}
                    />
                  );
                })}
                {Object.keys(this.state.filter.teacherIds).map((teacherId) => {
                  const teacher = this.state.filter.teacherIds[teacherId];
                  return (
                    <Chip
                      key={teacherId}
                      label={teacher.name}
                      onDelete={() => {
                        const filter = { ...this.state.filter };
                        delete filter.teacherIds[teacherId];
                        this.setState({ filter: filter });
                        const events = this.filterEvents(filter, this.state.allEvents);
                        this.setState({ events: events });
                      }}
                    />
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Grid container alignItems="center" className={classes.events} justify="center">
              {this.renderEvents()}
            </Grid>
          </Grid>
        </Grid>
      );
    }
  }
);
